import React from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { PageHero } from "../components/Sections";
import { Card } from "../components/ui/card";
import { Icon } from "../components/icons";
import { LeadForm } from "../components/LeadForm";
import { loanProducts, cities, cityLoanSlugs } from "../mock/data";
import { CheckCircle2, FileText, ClipboardCheck, Info, MapPin } from "lucide-react";

export default function CityLoan() {
  const { slug, city } = useParams();
  const p = loanProducts[slug];
  const cityObj = cities.find((c) => c.slug === city);
  if (!p || !cityObj || !cityLoanSlugs.includes(slug)) return <Navigate to={`/loans/${slug}`} replace />;

  const cityName = cityObj.name;
  const otherCities = cities.filter((c) => c.slug !== city);

  return (
    <div>
      <PageHero
        title={`${p.name} in ${cityName}`}
        subtitle={`Apply online for a ${p.name.toLowerCase()} in ${cityName} with assistance from our authorised lending partners. Approval, interest rate and tenure are decided by the lender.`}
        crumbs={[{ label: "Loans" }, { label: p.name, to: `/loans/${slug}` }, { label: cityName }]}
      />

      <section className="section-pad">
        <div className="container-page grid gap-12 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-10">
            <div className="flex items-start gap-4">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-secondary text-primary">
                <Icon name={p.icon} className="h-7 w-7" />
              </div>
              <div>
                <h2 className="font-heading text-2xl font-bold">{p.name} in {cityName}</h2>
                <p className="mt-2 text-muted-foreground">
                  Looking for a {p.name.toLowerCase()} in {cityName}? {p.intro} We help residents and businesses in {cityName}
                  connect with authorised lending partners, subject to eligibility.
                </p>
              </div>
            </div>

            <div>
              <h3 className="font-heading text-lg font-semibold flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-primary" /> Why apply through us in {cityName}
              </h3>
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                {p.features.map((f) => (
                  <div key={f} className="flex items-start gap-2.5 rounded-lg border bg-card p-3.5">
                    <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5 text-primary" />
                    <span className="text-sm">{f}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid gap-8 sm:grid-cols-2">
              <div>
                <h3 className="font-heading text-lg font-semibold flex items-center gap-2">
                  <ClipboardCheck className="h-5 w-5 text-primary" /> Eligibility
                </h3>
                <ul className="mt-4 space-y-2.5">
                  {p.eligibility.map((e) => (
                    <li key={e} className="flex items-start gap-2.5 text-sm">
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" /> {e}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="font-heading text-lg font-semibold flex items-center gap-2">
                  <FileText className="h-5 w-5 text-primary" /> Documents
                </h3>
                <ul className="mt-4 space-y-2.5">
                  {p.documents.map((d) => (
                    <li key={d} className="flex items-start gap-2.5 text-sm">
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" /> {d}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="flex items-start gap-3 rounded-xl border border-primary/20 bg-secondary/50 p-4">
              <Info className="h-5 w-5 shrink-0 text-primary" />
              <p className="text-sm text-muted-foreground">
                <strong className="text-foreground">Please note: </strong>{p.note} Submission of an enquiry does not
                guarantee approval. The lender in {cityName} will disclose the applicable interest rate, APR, processing
                fee, taxes, tenure, EMI and other charges through its Key Fact Statement.
              </p>
            </div>

            <div>
              <h3 className="font-heading text-lg font-semibold mb-3 flex items-center gap-2">
                <MapPin className="h-4 w-4 text-primary" /> {p.name} in other cities
              </h3>
              <div className="flex flex-wrap gap-2">
                {otherCities.map((c) => (
                  <Link key={c.slug} to={`/loans/${slug}/${c.slug}`}
                    className="rounded-full border px-4 py-2 text-sm hover:border-primary hover:text-primary transition-colors">
                    {p.name} in {c.name}
                  </Link>
                ))}
                <Link to={`/loans/${slug}`}
                  className="rounded-full border px-4 py-2 text-sm hover:border-primary hover:text-primary transition-colors">
                  All India
                </Link>
              </div>
            </div>
          </div>

          <aside className="lg:col-span-1">
            <Card className="sticky top-24 border p-6 shadow-sm">
              <h3 className="font-heading text-lg font-bold">Apply in {cityName}</h3>
              <p className="mt-1 text-sm text-muted-foreground">Get assistance with your {p.name.toLowerCase()} application.</p>
              <div className="mt-4">
                <LeadForm compact defaultType={p.name} />
              </div>
            </Card>
          </aside>
        </div>
      </section>
    </div>
  );
}
